import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Close,
  History,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { adventuresApi, AdventureChangeDto, AdventureHistoryResponse } from '@api/endpoints/adventures'

interface AdventureHistoryDialogProps {
  open: boolean
  onClose: () => void
  adventureId: string
  adventureTitle?: string
}

// Format date for display (server stores in UTC) - compact version
const formatDate = (dateString: string): string => {
  // Ensure the date string is treated as UTC if it doesn't have a timezone indicator
  const utcDateString = dateString.endsWith('Z') || dateString.includes('+') || dateString.includes('-', 10)
    ? dateString
    : dateString + 'Z'
  const date = new Date(utcDateString)
  const now = new Date()
  const showYear = date.getFullYear() !== now.getFullYear()

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(showYear && { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  })
}

// Truncate text for table display
const truncateValue = (value: string | null, maxLength: number = 30): string => {
  if (value === null || value === '') return '(empty)'
  if (value.length <= maxLength) return value
  return value.substring(0, maxLength) + '...'
}

// Get human-readable field name
const getFieldDisplayName = (fieldName: string): string => {
  const fieldMap: Record<string, string> = {
    Title: 'Title',
    Description: 'Description',
    QuickDescription: 'Quick Description',
    Location: 'Location',
    Latitude: 'Latitude',
    Longitude: 'Longitude',
    StartDate: 'Start Date',
    EndDate: 'End Date',
    IsPublic: 'Public/Private',
    IsRepeatable: 'Repeatable',
    AllowGroups: 'Allow Groups',
    MaxParticipants: 'Max Participants',
    DifficultyLevel: 'Difficulty Level',
    MainCategory: 'Main Category',
    UseDisplayForJoining: 'Use Display for Joining',
  }
  return fieldMap[fieldName] || fieldName
}

// Format value for display
const formatValue = (value: string | null, fieldName: string): string => {
  if (value === null || value === '') return '(empty)'

  // Handle boolean fields
  if (fieldName === 'IsPublic' || fieldName === 'IsRepeatable' || fieldName === 'AllowGroups' || fieldName === 'UseDisplayForJoining') {
    return value.toLowerCase() === 'true' ? 'Yes' : 'No'
  }

  // Handle date fields
  if (fieldName === 'StartDate' || fieldName === 'EndDate') {
    try {
      return formatDate(value)
    } catch {
      return value
    }
  }

  // Truncate long values
  if (value.length > 100) {
    return value.substring(0, 100) + '...'
  }

  return value
}

// Get field category color
const getFieldColor = (fieldName: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'default' => {
  if (['Title', 'Description', 'QuickDescription'].includes(fieldName)) return 'primary'
  if (['Location', 'Latitude', 'Longitude'].includes(fieldName)) return 'success'
  if (['StartDate', 'EndDate'].includes(fieldName)) return 'info'
  if (['IsPublic', 'IsRepeatable', 'AllowGroups'].includes(fieldName)) return 'warning'
  return 'default'
}

const PAGE_SIZE = 20

export function AdventureHistoryDialog({
  open,
  onClose,
  adventureId,
  adventureTitle,
}: AdventureHistoryDialogProps) {
  const [historyData, setHistoryData] = useState<AdventureHistoryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const toggleRowExpanded = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Fetch history when dialog opens or page changes
  useEffect(() => {
    if (!open || !adventureId) return

    const fetchHistory = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const skip = (page - 1) * PAGE_SIZE
        const response = await adventuresApi.getHistory(adventureId, {
          skip,
          take: PAGE_SIZE,
        })
        setHistoryData(response)
      } catch (err: any) {
        const message = err.response?.data?.message || err.message || 'Failed to load history'
        setError(message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchHistory()
  }, [open, adventureId, page])

  // Reset page when dialog opens
  useEffect(() => {
    if (open) {
      setPage(1)
    }
  }, [open])

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)
  }

  const totalPages = historyData ? Math.ceil(historyData.totalCount / PAGE_SIZE) : 0

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? undefined : '60vh', maxHeight: isMobile ? undefined : '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <History color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Change History
          </Typography>
          {(historyData?.adventureTitle || adventureTitle) && (
            <Typography variant="body2" color="text.secondary">
              {historyData?.adventureTitle || adventureTitle}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={isMobile ? { px: 0 } : undefined}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && historyData && (
          <>
            {historyData.changes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Changes Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no recorded changes for this adventure yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ width: '100%' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: isMobile ? '25%' : '18%' }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: isMobile ? '45%' : '44%' }}>Change</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: isMobile ? '30%' : '18%' }}>{isMobile ? 'Info' : 'User'}</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600, width: '20%' }}>Date</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.changes.map((change) => {
                      const oldDisplay = formatValue(change.oldValue, change.fieldName)
                      const newDisplay = formatValue(change.newValue, change.fieldName)
                      const oldTruncated = truncateValue(oldDisplay, isMobile ? 15 : 25)
                      const newTruncated = truncateValue(newDisplay, isMobile ? 15 : 25)
                      const isExpanded = expandedRows.has(change.id)

                      return (
                        <React.Fragment key={change.id}>
                          <TableRow
                            hover
                            onClick={isMobile ? () => toggleRowExpanded(change.id) : undefined}
                            sx={isMobile ? { cursor: 'pointer' } : undefined}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isMobile && (
                                  <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                  </Box>
                                )}
                                <Chip
                                  label={getFieldDisplayName(change.fieldName)}
                                  size="small"
                                  color={getFieldColor(change.fieldName)}
                                  sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              {!isMobile ? (
                                <Tooltip
                                  title={
                                    <Box>
                                      <Typography variant="caption" display="block"><strong>From:</strong> {oldDisplay}</Typography>
                                      <Typography variant="caption" display="block"><strong>To:</strong> {newDisplay}</Typography>
                                    </Box>
                                  }
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ cursor: 'help', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  >
                                    <Box component="span" sx={{ color: 'text.secondary' }}>{oldTruncated}</Box>
                                    <Box component="span" sx={{ mx: 0.5 }}>→</Box>
                                    <Box component="span" sx={{ fontWeight: 500 }}>{newTruncated}</Box>
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {newTruncated}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {isMobile ? (
                                <Box>
                                  <Typography variant="body2" noWrap>
                                    {change.changedByUserName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {formatDate(change.changedAt)}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2" noWrap title={change.changedByUserName}>
                                  {change.changedByUserName}
                                </Typography>
                              )}
                            </TableCell>
                            {!isMobile && (
                              <TableCell>
                                <Typography variant="body2" noWrap>
                                  {formatDate(change.changedAt)}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                          {isMobile && (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1.5, px: 1 }}>
                                    <Box sx={{ mb: 1 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        Previous Value:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          wordBreak: 'break-word',
                                          whiteSpace: 'pre-wrap',
                                          color: 'text.secondary',
                                        }}
                                      >
                                        {oldDisplay}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                        New Value:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          wordBreak: 'break-word',
                                          whiteSpace: 'pre-wrap',
                                        }}
                                      >
                                        {newDisplay}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {/* Total count */}
            {historyData.totalCount > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center', mt: 1 }}
              >
                Showing {historyData.skip + 1}-{Math.min(historyData.skip + historyData.changes.length, historyData.totalCount)} of {historyData.totalCount} changes
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

